import { useEffect, useRef, useState } from 'react'
import { Check, X } from '@phosphor-icons/react'
import { Tooltip } from '@/components/ui/Tooltip'

/**
 * Voice-to-text recorder for the AI workflow composer.
 *
 * - `getUserMedia` → Web Audio `AnalyserNode` drives a live waveform (bars scroll
 *   right→left; near-flat dots on silence, taller bars while speaking).
 * - Web Speech `SpeechRecognition` produces the transcript (text-only API, hence
 *   the separate audio stream for the visualisation).
 * - Confirm (✓) or auto-stop at 30s returns the transcript via `onComplete`.
 *   Cancel (✕) discards it.
 *
 * Caller is responsible for only mounting this when speech is supported
 * (see `isSpeechSupported`).
 */

const MAX_SECONDS = 30
const BAR_GAP = 2
const BAR_WIDTH = 2
const PUSH_INTERVAL_MS = 45 // how often a new amplitude sample enters the strip

export function isSpeechSupported(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as any
  return !!(w.SpeechRecognition || w.webkitSpeechRecognition) && !!navigator.mediaDevices?.getUserMedia
}

interface VoiceRecorderProps {
  onComplete: (transcript: string) => void
  onCancel: () => void
}

export function VoiceRecorder({ onComplete, onCancel }: VoiceRecorderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const recognitionRef = useRef<any>(null)
  const timerRef = useRef<number | null>(null)
  const finalRef = useRef('')
  const interimRef = useRef('')
  const barsRef = useRef<number[]>([])
  const lastPushRef = useRef(0)
  const doneRef = useRef(false)

  const [elapsed, setElapsed] = useState(0)

  // Stable callbacks via refs so the mount effect can run exactly once.
  const onCompleteRef = useRef(onComplete)
  const onCancelRef = useRef(onCancel)
  onCompleteRef.current = onComplete
  onCancelRef.current = onCancel

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (timerRef.current) clearInterval(timerRef.current)
    rafRef.current = null
    timerRef.current = null
    try { recognitionRef.current?.stop() } catch { /* already stopped */ }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    audioCtxRef.current?.close().catch(() => {})
  }

  const finish = () => {
    if (doneRef.current) return
    doneRef.current = true
    cleanup()
    const transcript = `${finalRef.current} ${interimRef.current}`.replace(/\s+/g, ' ').trim()
    onCompleteRef.current(transcript)
  }

  const cancel = () => {
    if (doneRef.current) return
    doneRef.current = true
    cleanup()
    onCancelRef.current()
  }

  const renderBars = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    if (w === 0 || h === 0) return
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    const isDark = document.documentElement.classList.contains('dark')
    ctx.fillStyle = isDark ? '#a5b4fc' : '#5e6ad2'

    const step = BAR_WIDTH + BAR_GAP
    const maxBars = Math.floor(w / step)
    const bars = barsRef.current
    const visible = bars.slice(-maxBars)
    const mid = h / 2
    for (let i = 0; i < visible.length; i++) {
      const amp = visible[i]
      const barH = Math.max(2, amp * (h - 2))
      // Anchor newest sample to the right edge → strip scrolls right→left.
      const x = w - (visible.length - i) * step
      const y = mid - barH / 2
      const r = Math.min(1, barH / 2)
      ctx.beginPath()
      if ((ctx as any).roundRect) (ctx as any).roundRect(x, y, BAR_WIDTH, barH, r)
      else ctx.rect(x, y, BAR_WIDTH, barH)
      ctx.fill()
    }
  }

  const draw = () => {
    const analyser = analyserRef.current
    if (analyser) {
      const buf = new Uint8Array(analyser.fftSize)
      analyser.getByteTimeDomainData(buf)
      let sum = 0
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / buf.length)
      const now = performance.now()
      if (now - lastPushRef.current > PUSH_INTERVAL_MS) {
        lastPushRef.current = now
        // Amplify so normal speech reaches a satisfying height; clamp to 1.
        barsRef.current.push(Math.min(1, rms * 2.4))
        if (barsRef.current.length > 400) barsRef.current.shift()
      }
    }
    renderBars()
    rafRef.current = requestAnimationFrame(draw)
  }

  useEffect(() => {
    let cancelledBeforeReady = false

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelledBeforeReady) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
        const ctx: AudioContext = new AudioCtx()
        audioCtxRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 1024
        analyser.smoothingTimeConstant = 0.6
        source.connect(analyser)
        analyserRef.current = analyser
        rafRef.current = requestAnimationFrame(draw)

        const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
        if (SR) {
          const rec = new SR()
          rec.continuous = true
          rec.interimResults = true
          rec.lang = navigator.language || 'en-US'
          rec.onresult = (e: any) => {
            let interim = ''
            for (let i = e.resultIndex; i < e.results.length; i++) {
              const chunk = e.results[i][0].transcript
              if (e.results[i].isFinal) finalRef.current += chunk + ' '
              else interim += chunk
            }
            interimRef.current = interim
          }
          rec.onerror = () => { /* no-mic / no-speech are non-fatal */ }
          recognitionRef.current = rec
          try { rec.start() } catch { /* race on rapid toggles */ }
        }

        timerRef.current = window.setInterval(() => {
          setElapsed((prev) => prev + 1)
        }, 1000)
      } catch {
        // Permission denied / no microphone — bail cleanly.
        cancel()
      }
    }

    start()
    return () => {
      cancelledBeforeReady = true
      cleanup()
    }
  }, [])

  // Auto-confirm at the 30s cap.
  useEffect(() => {
    if (elapsed >= MAX_SECONDS) finish()
  }, [elapsed])

  const mm = Math.floor(elapsed / 60)
  const ss = String(elapsed % 60).padStart(2, '0')

  return (
    <>
      <div className="flex-1 h-9 flex items-center gap-2 rounded-md border border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.04] px-3 overflow-hidden dark:border-[#5e6ad2]/40 dark:bg-[#5e6ad2]/[0.08]">
        <canvas ref={canvasRef} className="flex-1 h-6" />
        <span className="flex-shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground dark:text-[#8a8f98]">
          {mm}:{ss}
        </span>
      </div>
      <Tooltip label="Cancel">
        <button
          type="button"
          onClick={cancel}
          aria-label="Cancel"
          className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border border-border/60 text-muted-foreground hover:bg-muted/50 hover:text-foreground dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:bg-white/[0.04] dark:hover:text-[#f7f8f8] transition-colors duration-150"
        >
          <X className="h-4 w-4" />
        </button>
      </Tooltip>
      <Tooltip label="Done">
        <button
          type="button"
          onClick={finish}
          aria-label="Done"
          className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] transition-colors duration-150"
        >
          <Check className="h-4 w-4" weight="bold" />
        </button>
      </Tooltip>
    </>
  )
}

export default VoiceRecorder
